/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Checkbox, Select, Table } from 'antd';
import { CheckboxProps } from 'antd/lib/checkbox';
import { TableProps } from 'antd/lib/table';
import { Fragment, useState, useMemo, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import IconButton from '~components/_common/IconButton/IconButton';

// local imports
import { useFolders } from '~src/hooks/api-hooks/useFolders';
import { useSecurityGroups } from '~src/hooks/api-hooks/useSecurityGroups';
import { useIsSuperuser } from '~src/hooks/useIsSuperuser';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { CreateFolderPermissionParams } from '~src/services/api';
import { DbFolder, DbFolderPermission } from '~src/types/db';
import { RIGHTS_LEVEL } from '~src/types/enums';
import { getParentsIds } from '~src/utils/tree';
import {
  SecGroupOption,
  tabStyleFn,
  mapSecGroupsToSections,
} from './tabs.util';
import { TableEntry, arrToTree } from '~src/utils/securityGroupsConfig';
import { NavIcon } from '~components/_common/NavIcon/NavIcon';
import { getTenderIconArichive } from '~components/molecules/LeftSideView/utils';

export type AlteredDbFolderPermission = DbFolderPermission & {
  altered?: boolean;
};
export interface SingleGroupTabProps {
  folder?: DbFolder;
  permissionList: Array<{
    folderId: string | null;
    permissions: Array<
      AlteredDbFolderPermission | CreateFolderPermissionParams['permissionData']
    >;
  }>;
  onAddPermission: (
    permission: CreateFolderPermissionParams['permissionData']
  ) => void;
  onRemovePermission: (
    permission: CreateFolderPermissionParams['permissionData']
  ) => void;
  loading?: boolean;
}
/** Example component - use as template for creating components */
export const SingleGroupTab: React.FC<SingleGroupTabProps> = ({
  permissionList,
  loading,
  folder,
  onAddPermission,
  onRemovePermission,
}) => {
  const intl = useIntl();
  const style = useStyles(styleFn);
  const tabStyles = useStyles(tabStyleFn);

  const secGroupsQuery = useSecurityGroups({ enabled: false });
  const isSuperuser = useIsSuperuser();

  const foldersQuery = useFolders(
    {
      as_admin: isSuperuser ? true : undefined,
      show_deleted: false,
    },
    { enabled: false }
  );

  const [selectedGroup, setSelectedGroup] = useState<SecGroupOption>();
  const defaultExpandedKeys = useRef(
    folder
      ? [
          ...getParentsIds(foldersQuery.data || [], String(folder.id)),
          String(folder.id),
        ]
      : []
  );

  const secGroupsOptions = useMemo(
    () =>
      mapSecGroupsToSections(secGroupsQuery.data).map((section) => ({
        ...section,
        // translate section label
        label:
          intl.formatMessage({
            id: section.intlId,
            defaultMessage: section.label,
          }) || section.label,
      })),
    [secGroupsQuery.data, intl]
  );

  const tableData = useMemo(() => arrToTree(foldersQuery.data || [], null), [
    foldersQuery.data,
  ]);

  useEffect(() => {
    if (selectedGroup) return;
    secGroupsOptions[0] && setSelectedGroup(secGroupsOptions[0].options[0]);
  }, [secGroupsOptions, selectedGroup]);

  /** Filters security groups for specific row(folder) & column(access level) */
  const getValueForColumn = (folderId: string, level: RIGHTS_LEVEL) => {
    if (!selectedGroup) return false;

    return !!permissionList
      // get entry for folder
      .find((entry) => entry.folderId === folderId)
      // find permission matching security_group_id & level
      ?.permissions.find(
        (perm) =>
          perm.security_group_id === selectedGroup.key && perm.level === level
      );
  };

  const customReadTitle = () => {
    return (
      <div>
        {intl.formatMessage({ id: 'read' })}
        <IconButton
          icon={['far', 'info-circle']}
          customStyle={style.icons}
          containerStyle={{ marginRight: 5 }}
          backgroundOff
          tooltip={intl.formatMessage({ id: 'read-rights-info' })}
        />
      </div>
    );
  };

  const customUploadTitle = () => {
    return (
      <div>
        {intl.formatMessage({ id: 'upload' })}
        <IconButton
          icon={['far', 'info-circle']}
          customStyle={style.icons}
          containerStyle={{ marginRight: 5 }}
          backgroundOff
          tooltip={intl.formatMessage({ id: 'upload-rights-info' })}
        />
      </div>
    );
  };

  const customCreateAccessTitle = () => {
    return (
      <div>
        {intl.formatMessage({ id: 'create' })}
        <IconButton
          icon={['far', 'info-circle']}
          customStyle={style.icons}
          containerStyle={{ marginRight: 5 }}
          backgroundOff
          tooltip={intl.formatMessage({ id: 'create-rights-info' })}
        />
      </div>
    );
  };

  const customManageAccessTitle = () => {
    return (
      <div>
        {intl.formatMessage({ id: 'manage' })}
        <IconButton
          icon={['far', 'info-circle']}
          customStyle={style.icons}
          containerStyle={{ marginRight: 5 }}
          backgroundOff
          tooltip={intl.formatMessage({ id: 'manage-rights-info' })}
        />
      </div>
    );
  };

  const columns: TableProps<TableEntry>['columns'] = [
    {
      key: 'folderStructure',
      title: intl.formatMessage({ id: 'folder-structure' }),
      dataIndex: 'title',
      onCell: (record) => ({
        style: {
          whiteSpace: 'nowrap',
          overflow: 'auto',
          color:
            !isSuperuser && record.dbFolder.permission < RIGHTS_LEVEL.MANAGE
              ? 'rgba(0,0,0,0.25)'
              : undefined,
        },
      }),
      render: (title, record) => {
        const isTenderFolder = record.dbFolder?.option?.is_tender_folder;
        return {
          children: isTenderFolder ? (
            <Fragment>
              <NavIcon
                customStyle={{ width: '24px', marginRight: '6px' }}
                icon={getTenderIconArichive(record.dbFolder?.option)}
              />
              {title}
            </Fragment>
          ) : (
            <Fragment>
              <IconButton
                icon={['fas', 'folder']}
                customStyle={tabStyles.folderIcon}
                containerStyle={tabStyles.folderIconContainer}
                backgroundOff
              />
              {title}
            </Fragment>
          ),
        };
      },
    },
    {
      key: 'read',
      title: customReadTitle,
      width: 131,
      align: 'center',
      render: (x, record) => (
        <CheckBoxColumn
          securityGroupId={selectedGroup?.key}
          rightsLevel={RIGHTS_LEVEL.OPEN}
          folderId={+record.key}
          checked={getValueForColumn(record.key, RIGHTS_LEVEL.OPEN)}
          disabled={
            !isSuperuser && record.dbFolder.permission < RIGHTS_LEVEL.MANAGE
          }
          onAddPermission={onAddPermission}
          onRemovePermission={onRemovePermission}
        />
      ),
    },
    {
      key: 'upload',
      title: customUploadTitle,
      width: 131,
      align: 'center',
      render: (x, record) => (
        <CheckBoxColumn
          securityGroupId={selectedGroup?.key}
          rightsLevel={RIGHTS_LEVEL.UPLOAD}
          folderId={+record.key}
          checked={getValueForColumn(record.key, RIGHTS_LEVEL.UPLOAD)}
          disabled={
            !isSuperuser && record.dbFolder.permission < RIGHTS_LEVEL.MANAGE
          }
          onAddPermission={onAddPermission}
          onRemovePermission={onRemovePermission}
        />
      ),
    },
    {
      key: 'create',
      title: customCreateAccessTitle,
      width: 131,
      align: 'center',
      render: (x, record) => (
        <CheckBoxColumn
          securityGroupId={selectedGroup?.key}
          rightsLevel={RIGHTS_LEVEL.WRITE}
          folderId={+record.key}
          checked={getValueForColumn(record.key, RIGHTS_LEVEL.WRITE)}
          disabled={
            !isSuperuser && record.dbFolder.permission < RIGHTS_LEVEL.MANAGE
          }
          onAddPermission={onAddPermission}
          onRemovePermission={onRemovePermission}
        />
      ),
    },
    {
      key: 'manage',
      title: customManageAccessTitle,
      width: 131,
      align: 'center',
      render: (x, record) => (
        <CheckBoxColumn
          securityGroupId={selectedGroup?.key}
          rightsLevel={RIGHTS_LEVEL.MANAGE}
          folderId={+record.key}
          checked={getValueForColumn(record.key, RIGHTS_LEVEL.MANAGE)}
          disabled={
            !isSuperuser && record.dbFolder.permission < RIGHTS_LEVEL.MANAGE
          }
          onAddPermission={onAddPermission}
          onRemovePermission={onRemovePermission}
        />
      ),
    },
  ];

  return (
    <div css={style.container}>
      <Select
        css={{ minWidth: 220, marginBottom: 16 }}
        showSearch
        size="large"
        placeholder="Security Group"
        // search by `label`
        optionFilterProp="label"
        dropdownMatchSelectWidth={false}
        options={secGroupsOptions}
        disabled={loading}
        value={selectedGroup?.value}
        onSelect={(val, option) => setSelectedGroup(option as SecGroupOption)}
      />
      <Table<TableEntry>
        showSorterTooltip={false}
        bordered
        pagination={false}
        scroll={{ y: '50vh', x: 1090 }}
        expandable={{ defaultExpandedRowKeys: defaultExpandedKeys.current }}
        loading={loading}
        columns={columns}
        dataSource={tableData}
        rowClassName={(record) =>
          folder && record.key === String(folder.id)
            ? 'ant-table-row-selected'
            : ''
        }
      />
    </div>
  );
};

interface CheckBoxColumnProps extends Partial<CheckboxProps> {
  checked: boolean;
  rightsLevel: number;
  folderId: number;
  securityGroupId?: number;
  onAddPermission: SingleGroupTabProps['onAddPermission'];
  onRemovePermission: SingleGroupTabProps['onRemovePermission'];
}
const CheckBoxColumn: React.FC<CheckBoxColumnProps> = ({
  checked,
  rightsLevel,
  folderId,
  securityGroupId = 0,
  onAddPermission,
  onRemovePermission,
  ...checkboxProps
}) => (
  <Checkbox
    css={{ width: '100%' }}
    checked={checked}
    onChange={(e) =>
      e.target.checked
        ? onAddPermission({
            level: rightsLevel,
            folder_id: folderId,
            security_group_id: securityGroupId,
          })
        : onRemovePermission({
            level: rightsLevel,
            folder_id: folderId,
            security_group_id: securityGroupId,
          })
    }
    {...checkboxProps}
  />
);

const styleFn: ThemedStyleFn = (theme) => ({
  container: {},
});
